import React, { useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { labelFor } from "../lib/categoryMeta";
import ProductCard from "./ProductCard";
import CategoryMedia from "./CategoryMedia";

const MobileCategoryBrowse = ({
    parents,
    activeId,
    onSelect,
    onDisabledClick,
    activeParent,
    subFilter,
    onSubFilterChange,
    products,
    onBack,
}) => {
    const { t } = useI18n();
    // Hide empty parents entirely (user spec).
    parents = parents.filter((p) => !p.disabled);
    const railRef = useRef(null);
    const activeRef = useRef(null);

    useEffect(() => {
        if (activeRef.current && railRef.current) {
            activeRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, [activeId]);

    const labelOf = (p) => {
        const tr = t(p.name_key);
        return tr && tr !== p.name_key ? tr : p.label;
    };
    const activeLabel = activeParent ? labelOf(activeParent) : t("filter_all");

    return (
        <section
            data-testid="mobile-category-browse"
            className="md:hidden"
        >
            {/* Top bar */}
            <div className="sticky top-[112px] z-30 bg-white border-b border-neutral-200 px-3 py-2.5 flex items-center gap-2">
                <button
                    data-testid="mobile-back-btn"
                    onClick={onBack}
                    className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center"
                    aria-label="Back"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-medium tracking-tight truncate">
                        {activeLabel}
                    </h2>
                    <p className="text-[11px] text-neutral-500 leading-none">
                        {products.length} {t("filter_all").toLowerCase()}
                    </p>
                </div>
            </div>

            {/* Sub-category chips on mobile (only when parent has >1 child slug) */}
            {activeParent && activeParent.children.length > 1 && (
                <div
                    data-testid="mobile-sub-filter-row"
                    className="sticky top-[164px] z-20 bg-white border-b border-neutral-200 px-3 py-2 flex gap-1.5 overflow-x-auto no-scrollbar"
                >
                    <button
                        type="button"
                        onClick={() => onSubFilterChange("all")}
                        data-testid="mobile-sub-filter-all"
                        className={`h-8 px-3 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 transition-colors ${
                            subFilter === "all"
                                ? "bg-[#FF5722] text-white"
                                : "bg-neutral-100 text-neutral-700"
                        }`}
                    >
                        {t("sub_filter_all")}
                    </button>
                    {activeParent.children.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => onSubFilterChange(c.id)}
                            data-testid={`mobile-sub-filter-${c.id}`}
                            className={`h-8 px-3 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 transition-colors ${
                                subFilter === c.id
                                    ? "bg-[#FF5722] text-white"
                                    : "bg-neutral-100 text-neutral-700"
                            }`}
                        >
                            <span>{labelFor(c, t)}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="flex">
                {/* Left rail = parents */}
                <nav
                    ref={railRef}
                    data-testid="mobile-rail"
                    className="w-20 shrink-0 bg-neutral-50 border-r border-neutral-200 sticky overflow-y-auto no-scrollbar"
                    style={{ top: activeParent && activeParent.children.length > 1 ? "152px" : "104px", height: "calc(100vh - 104px)" }}
                >
                    {parents.map((p) => {
                        const label = labelOf(p);
                        if (p.disabled) {
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    data-testid={`mobile-rail-${p.id}-disabled`}
                                    onClick={() => onDisabledClick(label)}
                                    aria-disabled="true"
                                    className="w-full px-1 py-3 flex flex-col items-center gap-1 border-l-2 border-transparent text-neutral-400 opacity-60 cursor-not-allowed"
                                >
                                    <CategoryMedia parent={p} size="h-8 w-8" grayscale />
                                    <span className="text-[10px] font-semibold leading-tight text-center line-clamp-2">
                                        {label}
                                    </span>
                                    <span className="text-[8px] uppercase tracking-wider text-neutral-500 font-bold">
                                        {t("category_coming_soon")}
                                    </span>
                                </button>
                            );
                        }
                        return (
                            <button
                                key={p.id}
                                ref={activeId === p.id ? activeRef : null}
                                onClick={() => onSelect(p.id)}
                                data-testid={`mobile-rail-${p.id}`}
                                className={`w-full px-1 py-3 flex flex-col items-center gap-1 border-l-2 transition-colors ${
                                    activeId === p.id
                                        ? "border-[#FF5722] bg-white text-[#FF5722]"
                                        : "border-transparent text-neutral-700 hover:bg-white"
                                }`}
                            >
                                <CategoryMedia parent={p} size="h-8 w-8" />
                                <span className="text-[10px] font-semibold leading-tight text-center line-clamp-2">
                                    {label}
                                </span>
                                {p.items > 0 && (
                                    <span className="text-[9px] text-neutral-400 font-bold">
                                        {p.items}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Products */}
                <div className="flex-1 min-w-0 p-3">
                    {products.length === 0 ? (
                        <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center text-neutral-500 text-sm">
                            {t("section_no_products")}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default MobileCategoryBrowse;
